import { EquipmentModel, AssignedEquipmentModel } from './../../../../models/equipmentsModel';
import { SelectItem } from 'primeng/api';
import { Component, OnDestroy, OnInit } from '@angular/core';
import { Store } from '@ngrx/store';
import { State } from '../../../../core/store';
import { deepClone } from 'src/app/helpers/utils';
import { BaseComponent } from 'src/app/core/abstracts/baseComponent';
import {
  addEquipmentToAssign,
  removeEquipmentToAssign,
  searchEquipment,
  saveAssignEquipment,
  changedUserOnEquipmentToAssign,
  changedAssignedProgramOnEquipmentToAssign,
  AddReturnDate,
  RemoveReturnDate,
  SetReturnDate
} from 'src/app/core/store/equipment/equipment.actions';
import { formatDate } from '@angular/common';
import { ManageAssetService } from 'src/app/core/services/manage-asset.service';
@Component({
    selector: 'app-assign-equipment',
    templateUrl: './assign-equipment.component.html',
    styleUrls: ['./assign-equipment.component.scss'],
    standalone: false
})
export class AssignEquipmentComponent extends BaseComponent implements OnInit, OnDestroy {
  readonly assignmentTargetOptions: SelectItem[] = [
    { label: 'User', value: 'user' },
    { label: 'Campus', value: 'campus' }
  ];
  assetGroupFilterOptions: SelectItem[] = [{ label: 'No group filter', value: '' }];
  canEdit: boolean;
  assigningEquipments: AssignedEquipmentModel[];
  usersList: SelectItem[] = [{ label: 'loading...', value: undefined }];
  campusesList: SelectItem[] = [{ label: 'loading...', value: undefined }];
  selectedEquipment: EquipmentModel;
  equipmentSearchResult: EquipmentModel[];
  assignmentTargetType: 'user' | 'campus' = 'user';
  userID: number;
  assignedProgramID: number;
  myUserID: number;
  showErrors = false;
  savingAssignEquipments = false;
  assignButtonText: string = 'Assign';
  selectedEquipmentID: number;
  currentDate: string;
  assignmentNote = '';
  assetGroupFilter = '';
  scanBarcodes = '';
  scanMessages: string[] = [];
  scannedAssets: { barcode: string; count: number; matches: EquipmentModel[] }[] = [];
  scanLookupPending: string[] = null;
  assignmentQuantityError = '';
  locationList: SelectItem[] = [{ label: 'All locations', value: null }];
  inventoryAssetLocationID: number;
  groupSeedAsset: EquipmentModel = null;
  groupMembers: EquipmentModel[] = [];
  groupMemberSearchResults: EquipmentModel[] = [];
  selectedGroupMember: EquipmentModel = null;
  loadingGroupMembers = false;
  showGroupMemberStep = false;
  groupMemberStepCollapsed = false;
  includeTerminated: boolean = false;
  showAssignConfirmDialog = false;
  private collapsedGroupCategories = new Set<string>();
  private suppressedGroupSeedIdentity: string = null;
  private autoAddAttemptedForSeedIdentity = new Set<string>();

  constructor(
    private store: Store<State>,
    private manageAssetService: ManageAssetService,
  ) {
    super();
    this.currentDate = new Date().toISOString().slice(0, 10);
  }

  ngOnInit() {
    this.loadAssignableUsers();
    this.subs.push(
      this.store.select(s => s.accountState.user?.permissionIDs).subscribe(permissionIDs => {
        this.canEdit = permissionIDs && !!permissionIDs.find(p => p === 3058); //manage equipment
      }),
      this.store.select(s => s.accountState.user).subscribe(myUser => {
        this.myUserID = myUser.id;
      }),
      this.store.select(s => s.equipmentState.assigningEquipments).subscribe(assigningEquipments => {
        this.assigningEquipments = deepClone(assigningEquipments);
        this.selectedEquipment = null;

        if (this.assigningEquipments.length > 0) {
          const firstItem = this.assigningEquipments[0];
          const normalizedUserID = this.normalizeUserID(firstItem.userID) ?? this.normalizeUserID(this.userID);
          const normalizedProgramID = this.normalizeProgramID(firstItem.assignedProgramID) ?? this.normalizeProgramID(this.assignedProgramID);

          this.userID = normalizedUserID;
          this.assignedProgramID = normalizedProgramID;

          // Preserve the user's explicit target-type toggle when no assignee is selected yet.
          if (this.assignedProgramID) {
            this.assignmentTargetType = 'campus';
          } else if (this.userID) {
            this.assignmentTargetType = 'user';
          }
          if (this.assignmentTargetType === 'user' && this.userID) {
            this.ensureSelectedUserAssignable();
          }
          this.assignmentNote = this.assigningEquipments[0].returnNotes ?? '';
          this.syncGroupMemberStepFromCurrentSelection();
        } else {
          this.userID = null;
          this.assignedProgramID = null;
          this.includeTerminated = false;
          this.loadAssignableUsers();
          this.assignmentNote = '';
          this.suppressedGroupSeedIdentity = null;
          this.autoAddAttemptedForSeedIdentity.clear();
          this.closeGroupMemberStep(false);
        }

        this.assignButtonText = 'Assign';
      }),
      this.store.select(s => s.equipmentState.savingAssignEquipments).subscribe(savingAssignEquipments => {
        this.savingAssignEquipments = savingAssignEquipments;
      }),

      this.store.select(s => s.equipmentState.equipmentSearchResult).subscribe(equipmentSearchResult => {
        this.equipmentSearchResult = equipmentSearchResult;
      }),
    );
    this.manageAssetService.getAssignableCampuses().subscribe(campuses => {
      this.campusesList = (campuses ?? []).map(campus => ({
        label: campus.programName,
        value: campus.id
      }));
      this.campusesList.unshift({ label: '', value: null });
    });
    this.manageAssetService.getAssetLocations().subscribe(locations => {
      const mappedLocations = (locations ?? []).map(location => ({
        label: `${location.name} (${location.programName})`,
        value: location.id
      }));
      this.locationList = [{ label: 'All locations', value: null }, ...mappedLocations];
    });
    this.manageAssetService.getAssetGroups().subscribe(groups => {
      this.assetGroupFilterOptions = this.buildGroupFilterOptions(groups ?? []);
    });
  }

  requestSave() {
    if (this.disableAssignControls) {
      return;
    }
    this.assignmentQuantityError = this.getAssignmentQuantityError();
    if (this.assignmentQuantityError) {
      return;
    }

    const selectedUserID = this.getSelectedUserID();
    const selectedProgramID = this.getSelectedProgramID();
    const hasSelectedTarget = this.assignmentTargetType === 'campus'
      ? !!selectedProgramID
      : !!selectedUserID;

    if (hasSelectedTarget && this.assigningEquipments.length > 0) {
      this.showErrors = false;
      this.showAssignConfirmDialog = true;
    } else {
      this.showErrors = true;
    }
  }

  save() {
    if (this.disableAssignControls) {
      return;
    }
    this.assignmentQuantityError = this.getAssignmentQuantityError();
    if (this.assignmentQuantityError) {
      this.showAssignConfirmDialog = false;
      return;
    }

    const selectedUserID = this.getSelectedUserID();
    const selectedProgramID = this.getSelectedProgramID();

    const assigningEquipments = this.assigningEquipments.map(item => ({
      ...item,
      userID: this.assignmentTargetType === 'user' ? selectedUserID : null,
      assignedProgramID: this.assignmentTargetType === 'campus' ? selectedProgramID : null,
      returnNotes: this.assignmentNote?.trim() || undefined
    }));

    this.store.dispatch(saveAssignEquipment({ assigningEquipments }));
    this.showAssignConfirmDialog = false;
  }

  cancelSave() {
    if (this.disableAssignControls) {
      return;
    }

    this.showAssignConfirmDialog = false;
  }
  
  includeTerminatedChange(event){
    this.includeTerminated = !!event?.checked;
    this.loadAssignableUsers();
  }

  removeEquipment(equipment: AssignedEquipmentModel) {
    const removedIdentity = this.getAssetIdentity(equipment?.equipment);
    const assignedEquipment: AssignedEquipmentModel = {
      userID: this.userID,
      equipmentID: equipment.equipmentID ?? equipment.id,
      assetItemID: equipment.assetItemID,
      assetLocationID: equipment.equipment?.inventoryAssetLocationID
    };
    this.store.dispatch(removeEquipmentToAssign({ assignedEquipment }));
    this.autoAddAttemptedForSeedIdentity.delete(removedIdentity);
    if (this.groupSeedAsset && removedIdentity === this.getAssetIdentity(this.groupSeedAsset)) {
      this.closeGroupMemberStep();
    }
  }

  searchEquipment(event) {
    this.store.dispatch(searchEquipment({
      filter: {
        searchText: event.query,
        inventoryAssetLocationID: this.inventoryAssetLocationID || null,
        groupFilter: this.assetGroupFilter || undefined
      }
    }));
  }

  lookupScannedAssets() {
    if (this.scanLookupPending) {
      return;
    }
    const codes = (this.scanBarcodes || '').split(/[\s,;]+/).filter(Boolean);
    if (!codes.length || codes.length > 100) {
      this.scanMessages = ['Enter between 1 and 100 barcodes.'];
      return;
    }
    this.scanMessages = [];
    this.scannedAssets = [];
    this.scanLookupPending = codes;
    const submittedValue = this.scanBarcodes;
    this.manageAssetService.searchAvailableAssets({ barcodes: codes, includeTrackedItems: true, includeInventoryAssets: true }).subscribe({
      next: results => {
        if (this.scanBarcodes !== submittedValue) {
          return;
        }
        const mapped = (results || []).map(item => this.mapSearchResultToEquipment(item));
        mapped.forEach(item => {
          const identifiers = [item.barcode, item.assetGUID]
            .map(code => (code || '').replace(/-/g, '').toUpperCase());
          const scanCount = codes.filter(code => identifiers.includes(code.replace(/-/g, '').toUpperCase())).length;
          const matchingLocations = mapped.filter(candidate => candidate.assetInventoryID === item.assetInventoryID).length;
          item.scanCount = scanCount;
          item.scanQuantity = item.assetItemID ? 1 : matchingLocations === 1
            ? Math.min(scanCount, item.quantityAvailable ?? 0) : 1;
        });
        const uniqueCodes = [...new Set(codes.map(code => code.replace(/-/g, '').toUpperCase()))];
        this.scannedAssets = uniqueCodes.map(normalized => {
          const count = codes.filter(code => code.replace(/-/g, '').toUpperCase() === normalized).length;
          const matches = mapped.filter(item => [item.barcode, item.assetGUID]
            .some(code => (code || '').replace(/-/g, '').toUpperCase() === normalized));
          return {
            barcode: codes.find(code => code.replace(/-/g, '').toUpperCase() === normalized),
            count,
            matches
          };
        });
        this.scanMessages = this.scannedAssets.filter(row => !row.matches.length)
          .map(row => `${row.barcode}: Asset not found or access is restricted.`);
      },
      error: err => {
        this.scanMessages = [typeof err?.error === 'string' ? err.error : err?.error?.message || 'Unable to look up scanned assets.'];
        this.scanLookupPending = null;
      },
      complete: () => { this.scanLookupPending = null; }
    });
  }

  isAlreadyAssigned(equipment) {
    if (!equipment) {
      return true;
    }

    const equipmentIdentity = this.getAssetIdentity(equipment);
    return !this.assigningEquipments?.some(x => this.getAssignedEquipmentIdentity(x) === equipmentIdentity);
  }

  addEquipment(equipment: EquipmentModel) {
    this.queueEquipmentForAssignment(equipment, true);
  }

  canAddScannedEquipment(item: EquipmentModel): boolean {
    if (item.canSelect === false || !this.isAlreadyAssigned(item)) {
      return false;
    }
    const quantity = item.assetItemID ? 1 : Number(item.scanQuantity);
    const identifiers = [item.barcode, item.assetGUID].filter(code => !!code)
      .map(code => (code || '').replace(/-/g, '').toUpperCase());
    const alreadyQueued = (this.assigningEquipments ?? [])
      .filter(selected => [selected.barcode, selected.equipment?.assetGUID]
        .some(code => !!code && identifiers.includes(code.replace(/-/g, '').toUpperCase())))
      .reduce((sum, selected) => sum + (selected.quantityToAssign ?? 1), 0);
    return Number.isInteger(quantity) && quantity > 0
      && quantity <= (item.assetItemID ? 1 : item.quantityAvailable ?? 0)
      && alreadyQueued + quantity <= (item.scanCount ?? 0);
  }

  addScannedEquipment(item: EquipmentModel) {
    if (!this.canAddScannedEquipment(item)) {
      return;
    }
    this.queueEquipmentForAssignment(item, true, false, item.assetItemID ? 1 : item.scanQuantity, !item.assetItemID);
  }

  get selectedUnitCount(): number {
    return (this.assigningEquipments ?? []).reduce((sum, item) => sum + (item.quantityToAssign ?? 1), 0);
  }

  private getAssignmentQuantityError(): string {
    const invalid = (this.assigningEquipments ?? []).find(item => {
      const quantity = item.quantityToAssign ?? 1;
      return !Number.isInteger(quantity) || quantity < 1
        || (!!item.assetItemID && quantity !== 1)
        || (item.requireExactAssetLocation && quantity > (item.equipment?.quantityAvailable ?? 0));
    });
    return invalid ? `Review the quantity and available stock for ${invalid.description || invalid.barcode || 'the selected asset'}.` : '';
  }

  addGroupMember(equipment: EquipmentModel) {
    if (!this.canSelectGroupMember(equipment)) {
      return;
    }
    this.queueEquipmentForAssignment(equipment, false, false);
    this.collapseCategoryForMember(equipment);
    this.selectedGroupMember = null;
  }

  canSelectGroupMember(equipment: EquipmentModel | null | undefined): boolean {
    return !!equipment && this.isAlreadyAssigned(equipment) && equipment.canSelect !== false;
  }

  completeGroupMemberSearch(event: { query: string }) {
    const query = `${event?.query ?? ''}`.trim().toLowerCase();
    if (!query) {
      this.groupMemberSearchResults = this.groupMembers.slice(0);
      return;
    }
    this.groupMemberSearchResults = this.groupMembers.filter(item =>
      `${item.label ?? ''}`.toLowerCase().includes(query)
      || `${item.description ?? ''}`.toLowerCase().includes(query)
      || `${item.barcode ?? ''}`.toLowerCase().includes(query)
      || `${item.serialNumber ?? ''}`.toLowerCase().includes(query)
      || `${item.unavailableReason ?? ''}`.toLowerCase().includes(query));
  }

  onAssetGroupFilterChange() {
    this.selectedEquipment = null;
    this.equipmentSearchResult = [];
  }

  closeGroupMemberStep(suppressReopen = true) {
    if (suppressReopen && this.groupSeedAsset) {
      this.suppressedGroupSeedIdentity = this.getAssetIdentity(this.groupSeedAsset);
    }
    this.showGroupMemberStep = false;
    this.groupSeedAsset = null;
    this.groupMembers = [];
    this.groupMemberSearchResults = [];
    this.selectedGroupMember = null;
    this.groupMemberStepCollapsed = false;
    this.collapsedGroupCategories.clear();
    this.loadingGroupMembers = false;
  }

  toggleGroupMemberStepCollapsed() {
    this.groupMemberStepCollapsed = !this.groupMemberStepCollapsed;
  }

  get groupedSeedDisplayName() {
    if (!this.groupSeedAsset) {
      return '';
    }
    const groupNames = this.groupSeedAsset.assetGroups?.length
      ? this.groupSeedAsset.assetGroups.join(', ')
      : 'Asset Group';
    return `${groupNames} - ${this.groupSeedAsset.description ?? this.groupSeedAsset.label ?? ''}`.trim();
  }

  get groupMembersByCategory() {
    const categoryMap = new Map<string, EquipmentModel[]>();
    for (const member of this.groupMembers ?? []) {
      const category = (member.assetCategory || 'Uncategorized').trim();
      const rows = categoryMap.get(category) ?? [];
      rows.push(member);
      categoryMap.set(category, rows);
    }
    return Array.from(categoryMap.entries())
      .map(([category, items]) => ({
        category,
        items: items.slice(0).sort((a, b) => `${a.description ?? ''}`.localeCompare(`${b.description ?? ''}`))
      }))
      .sort((a, b) => a.category.localeCompare(b.category));
  }

  get groupMemberCategoryStatus() {
    return this.groupMembersByCategory.map(group => {
      const selectableCount = group.items.filter(item => item.canSelect !== false).length;
      const selectedCount = group.items.filter(item => !this.isAlreadyAssigned(item)).length;
      const isUnavailableCategory = selectableCount < 1 && selectedCount < 1;
      return {
        category: group.category,
        totalCount: group.items.length,
        selectableCount,
        selectedCount,
        isIncomplete: selectedCount < 1,
        isUnavailableCategory
      };
    });
  }

  get hasIncompleteGroupedKitSelection(): boolean {
    return this.groupMemberCategoryStatus.some(x => x.isIncomplete);
  }

  get hasUnavailableGroupedKitCategories(): boolean {
    return this.groupMemberCategoryStatus.some(x => x.isUnavailableCategory);
  }

  get incompleteGroupedKitCategorySummaries() {
    return this.groupMemberCategoryStatus
      .filter(x => x.isIncomplete)
      .map(x => ({
        category: x.category,
        selectedCount: x.selectedCount,
        expectedCount: x.totalCount,
        hasNoSelectableOptions: x.isUnavailableCategory
      }));
  }

  getCategoryUnavailableMessage(category: string): string | null {
    const categoryStatus = this.groupMemberCategoryStatus
      .find(x => this.getCategoryKey(x.category) === this.getCategoryKey(category));
    if (!categoryStatus || !categoryStatus.isUnavailableCategory) {
      return null;
    }
    return 'No available accessories in this category right now.';
  }

  getGroupMemberStatusLabel(member: EquipmentModel): string {
    if (!this.isAlreadyAssigned(member)) {
      return this.isAutoAddedSelection(member) ? 'Selected (auto)' : 'Selected';
    }
    if (member.canSelect === false) {
      return member.unavailableReason || 'Unavailable';
    }
    return 'Available';
  }

  isCategoryCollapsed(category: string): boolean {
    return this.collapsedGroupCategories.has(this.getCategoryKey(category));
  }

  toggleCategoryCollapsed(category: string) {
    const key = this.getCategoryKey(category);
    if (this.collapsedGroupCategories.has(key)) {
      this.collapsedGroupCategories.delete(key);
      return;
    }
    this.collapsedGroupCategories.add(key);
  }

  getSelectedCountForCategory(category: string): number {
    const categoryKey = this.getCategoryKey(category);
    return (this.groupMembers ?? [])
      .filter(member => this.getCategoryKey(member?.assetCategory || 'Uncategorized') === categoryKey)
      .filter(member => !this.isAlreadyAssigned(member))
      .length;
  }

  private queueEquipmentForAssignment(equipment: EquipmentModel, openGroupStep: boolean, autoAddedFromGroup = false,
    quantityToAssign = 1, requireExactAssetLocation = false) {
    if (!equipment) {
      return;
    }

    if (!this.isAlreadyAssigned(equipment)) {
      return;
    }
    if (equipment.canSelect === false) {
      return;
    }

    const selectedUserID = this.getSelectedUserID();
    const selectedProgramID = this.getSelectedProgramID();
    const assignedEquipment: AssignedEquipmentModel = {
      userID: this.assignmentTargetType === 'user' ? selectedUserID : null,
      assignedProgramID: this.assignmentTargetType === 'campus' ? selectedProgramID : null,
      equipmentID: equipment.id,
      assetInventoryID: equipment.assetInventoryID ?? equipment.id,
      assetItemID: equipment.assetItemID,
      isInventoryAssignment: equipment.isInventoryAssignment,
      quantityToAssign,
      requireExactAssetLocation,
      assignedByUserID: this.myUserID,
      dateAssignedString: formatDate(new Date(), 'yyyy-MM-dd', 'en-US'),
      description: equipment.description,
      assetCategory: equipment.assetCategory,
      barcode: equipment.barcode,
      inventoryAssetLocation: equipment.inventoryAssetLocation,
      assetStatus: equipment.requireIndividualTracking ? 'Available' : `Available (${equipment.quantityAvailable})`,
      autoAddedFromGroup,
      equipment
    };
    this.selectedEquipmentID = equipment.id;
    this.store.dispatch(addEquipmentToAssign({ assignedEquipment }));
    this.selectedEquipment = null;

    if (openGroupStep && equipment.isGrouped) {
      this.openGroupMemberStep(equipment, true, true);
    }
  }

  private openGroupMemberStep(seedAsset: EquipmentModel, forceOpen = false, autoAddAccessories = false) {
    const seedIdentity = this.getAssetIdentity(seedAsset);
    if (!forceOpen && this.suppressedGroupSeedIdentity === seedIdentity) {
      return;
    }

    this.suppressedGroupSeedIdentity = null;
    this.groupSeedAsset = seedAsset;
    this.showGroupMemberStep = true;
    this.groupMemberStepCollapsed = false;
    this.loadingGroupMembers = true;
    this.groupMembers = [];
    this.groupMemberSearchResults = [];
    this.selectedGroupMember = null;

    this.manageAssetService.getGroupMembersForSelection({
      assetInventoryID: seedAsset.assetInventoryID ?? seedAsset.id,
      inventoryAssetLocationID: this.inventoryAssetLocationID || null,
      includeTrackedItems: true,
      includeInventoryAssets: true
    }).subscribe({
      next: (results) => {
        const mapped = (results ?? [])
          .map(x => this.mapSearchResultToEquipment(x));
        this.groupMembers = mapped;
        this.groupMemberSearchResults = mapped.slice(0);
        this.collapsedGroupCategories.clear();
        const seedIdentity = this.getAssetIdentity(seedAsset);
        const shouldAutoAdd = autoAddAccessories || !this.autoAddAttemptedForSeedIdentity.has(seedIdentity);
        if (shouldAutoAdd) {
          this.autoAddAttemptedForSeedIdentity.add(seedIdentity);
          this.autoAddGroupAccessories();
        }
        this.collapseCategoriesWithAutoSelections();
      },
      error: () => {
        this.groupMembers = [];
        this.groupMemberSearchResults = [];
      },
      complete: () => {
        this.loadingGroupMembers = false;
      }
    });
  }

  private syncGroupMemberStepFromCurrentSelection() {
    const seedAsset = this.assigningEquipments?.[0]?.equipment;
    if (!seedAsset || !seedAsset.isGrouped) {
      if (this.showGroupMemberStep) {
        this.closeGroupMemberStep(false);
      }
      this.suppressedGroupSeedIdentity = null;
      return;
    }

    const seedIdentity = this.getAssetIdentity(seedAsset);
    const currentSeedIdentity = this.getAssetIdentity(this.groupSeedAsset);
    if (this.showGroupMemberStep && currentSeedIdentity === seedIdentity) {
      this.groupMemberSearchResults = this.groupMembers.slice(0);
      return;
    }

    this.openGroupMemberStep(seedAsset);
  }

  private mapSearchResultToEquipment(asset: any): EquipmentModel {
    const labelParts = [
      asset.assetType,
      asset.assetCategory,
      asset.description,
      asset.serialNumber,
      asset.barcode
    ].filter(x => !!x);

    return {
      id: asset.assetItemID ?? asset.assetInventoryID,
      assetInventoryID: asset.assetInventoryID,
      assetItemID: asset.assetItemID,
      isInventoryAssignment: asset.isInventoryAssignment,
      assetTypeID: asset.assetTypeID,
      assetCategory: asset.assetCategory,
      description: asset.description,
      barcode: asset.barcode,
      assetGUID: asset.assetGUID,
      inventoryAssetLocation: asset.inventoryAssetLocation,
      inventoryAssetLocationID: asset.inventoryAssetLocationID,
      programName: asset.programName,
      quantityAvailable: asset.quantityAvailable,
      requireIndividualTracking: asset.requireIndividualTracking,
      canSelect: asset.canSelect,
      unavailableReason: asset.unavailableReason,
      isGrouped: asset.isGrouped,
      groupMemberCount: asset.groupMemberCount,
      assetGroupIDs: asset.assetGroupIDs ?? [],
      assetGroups: asset.assetGroups ?? [],
      serialNumber: asset.serialNumber ?? '',
      brand: asset.assetCategory ?? '',
      model: asset.description ?? '',
      serviceTag: '',
      expressServiceCode: '',
      type: asset.assetType,
      purchaseDate: null,
      assetTag: asset.barcode ?? '',
      label: labelParts.join(' - '),
      archived: false,
      department: '',
      pricing: '',
      purchaseVendor: '',
      purchaseLocation: '',
      storedLocation: asset.inventoryAssetLocation ?? '',
      storedLocationID: asset.inventoryAssetLocationID ?? 0,
      storedOther: '',
      paymentMethod: '',
      size: '',
      classCode: '',
      classification: '',
      classificationOther: '',
      mainProgramID: 0,
      mainProgramName: asset.programName ?? '',
      equipmentTypeID: asset.assetTypeID
    };
  }

  private getAssetIdentity(equipment: EquipmentModel): string {
    if (!equipment) {
      return '';
    }
    const assetInventoryID = equipment.assetInventoryID ?? equipment.id ?? 0;
    const assetItemID = equipment.assetItemID ?? 0;
    if (assetItemID > 0) {
      return `${assetInventoryID}|item|${assetItemID}`;
    }

    const inventoryAssetLocationID = equipment.inventoryAssetLocationID ?? 0;
    const inventoryAssetLocation = `${equipment.inventoryAssetLocation ?? ''}`.trim().toLowerCase();
    return `${assetInventoryID}|inventory|${inventoryAssetLocationID}|${inventoryAssetLocation}`;
  }

  private getAssignedEquipmentIdentity(assignedEquipment: AssignedEquipmentModel): string {
    if (!assignedEquipment) {
      return '';
    }

    if (assignedEquipment.equipment) {
      return this.getAssetIdentity(assignedEquipment.equipment);
    }

    const fallbackEquipment: EquipmentModel = {
      id: assignedEquipment.equipmentID,
      assetInventoryID: assignedEquipment.assetInventoryID ?? assignedEquipment.equipmentID,
      assetItemID: assignedEquipment.assetItemID,
      isInventoryAssignment: assignedEquipment.isInventoryAssignment,
      inventoryAssetLocationID: assignedEquipment.assetLocationID,
      inventoryAssetLocation: assignedEquipment.inventoryAssetLocation ?? '',
      serialNumber: '',
      brand: '',
      model: '',
      serviceTag: '',
      expressServiceCode: '',
      type: '',
      purchaseDate: null,
      assetTag: '',
      label: '',
      archived: false,
      department: '',
      pricing: '',
      purchaseVendor: '',
      purchaseLocation: '',
      storedLocation: '',
      storedLocationID: 0,
      storedOther: '',
      paymentMethod: '',
      size: '',
      classCode: '',
      classification: '',
      classificationOther: '',
      mainProgramID: 0,
      mainProgramName: '',
      equipmentTypeID: 0
    };
    return this.getAssetIdentity(fallbackEquipment);
  }

  private collapseCategoryForMember(member: EquipmentModel) {
    this.collapsedGroupCategories.add(this.getCategoryKey(member?.assetCategory || 'Uncategorized'));
  }

  private autoAddGroupAccessories() {
    const queuedIdentities = new Set(
      (this.assigningEquipments ?? []).map(item => this.getAssignedEquipmentIdentity(item))
    );
    const categories = this.groupMembersByCategory;
    categories.forEach(categoryGroup => {
      // Keep one selected asset per category when auto-building grouped kits.
      if (this.hasSelectedAssetInCategory(categoryGroup.category)) {
        return;
      }
      const candidate = categoryGroup.items.find(item =>
        item?.canSelect !== false && !queuedIdentities.has(this.getAssetIdentity(item)));
      if (!candidate) {
        return;
      }
      this.queueEquipmentForAssignment(candidate, false, true);
      queuedIdentities.add(this.getAssetIdentity(candidate));
      this.collapseCategoryForMember(candidate);
    });
  }

  private hasSelectedAssetInCategory(category: string): boolean {
    const categoryKey = this.getCategoryKey(category);
    return (this.assigningEquipments ?? [])
      .some(item => this.getCategoryKey(item?.equipment?.assetCategory || 'Uncategorized') === categoryKey);
  }

  private collapseCategoriesWithAutoSelections() {
    (this.groupMembersByCategory ?? []).forEach(categoryGroup => {
      const hasAutoSelection = categoryGroup.items.some(member => this.isAutoAddedSelection(member));
      if (!hasAutoSelection) {
        return;
      }
      this.collapsedGroupCategories.add(this.getCategoryKey(categoryGroup.category));
    });
  }

  private getCategoryKey(category: string): string {
    return `${category ?? ''}`.trim().toLowerCase();
  }

  private isAutoAddedSelection(member: EquipmentModel): boolean {
    const identity = this.getAssetIdentity(member);
    return (this.assigningEquipments ?? []).some(item =>
      this.getAssignedEquipmentIdentity(item) === identity && item.autoAddedFromGroup === true);
  }

  setReturnDate(equipment){
    const assignedEquipment: AssignedEquipmentModel = {
      userID: this.userID,
      equipmentID: equipment.equipment.id,
      assetInventoryID: equipment.assetInventoryID,
      assetItemID: equipment.assetItemID,
      assignedByUserID: this.myUserID,
      dateAssignedString: formatDate(new Date(), 'yyyy-MM-dd', 'en-US'),
      returnDateString: equipment.returnDateString,
      equipment: equipment.equipment
    };
    this.store.dispatch(SetReturnDate({assignedEquipment}));
  }

  removeReturnDate(equipment){
    const assignedEquipment: AssignedEquipmentModel = {
      userID: this.userID,
      equipmentID: equipment.equipment.id,
      assetInventoryID: equipment.assetInventoryID,
      assetItemID: equipment.assetItemID,
      assignedByUserID: this.myUserID,
      dateAssignedString: formatDate(new Date(), 'yyyy-MM-dd', 'en-US'),
      equipment: equipment.equipment
    };
    this.store.dispatch(RemoveReturnDate({assignedEquipment}));
  }

  AddReturnDate(equipment){
    const assignedEquipment: AssignedEquipmentModel = {
      userID: this.userID,
      equipmentID: equipment.equipment.id,
      assetInventoryID: equipment.assetInventoryID,
      assetItemID: equipment.assetItemID,
      assignedByUserID: this.myUserID,
      dateAssignedString: formatDate(new Date(), 'yyyy-MM-dd', 'en-US'),
      equipment: equipment.equipment
    };
    this.store.dispatch(AddReturnDate({assignedEquipment}));
  }

  changedUser() {
    this.userID = this.normalizeUserID(this.userID);
    this.assignmentTargetType = 'user';
    this.assignedProgramID = null;

    if (this.hasSelectedAssignmentTarget()) {
      this.showErrors = false;
    }

    if (this.assigningEquipments?.length > 0) {
      this.store.dispatch(changedUserOnEquipmentToAssign({ userID: this.userID }));
      this.store.dispatch(changedAssignedProgramOnEquipmentToAssign({ assignedProgramID: null }));
    }
  }

  changedAssignmentTarget() {
    if (this.assignmentTargetType === 'campus') {
      this.userID = null;
      if (this.assigningEquipments?.length > 0) {
        this.store.dispatch(changedUserOnEquipmentToAssign({ userID: null }));
      }
      return;
    }

    this.assignedProgramID = null;
    if (this.assigningEquipments?.length > 0) {
      this.store.dispatch(changedAssignedProgramOnEquipmentToAssign({ assignedProgramID: null }));
    }
  }

  changedCampus() {
    this.assignedProgramID = this.normalizeProgramID(this.assignedProgramID);
    this.assignmentTargetType = 'campus';
    this.userID = null;

    if (this.hasSelectedAssignmentTarget()) {
      this.showErrors = false;
    }

    if (this.assigningEquipments?.length > 0) {
      this.store.dispatch(changedAssignedProgramOnEquipmentToAssign({ assignedProgramID: this.assignedProgramID }));
      this.store.dispatch(changedUserOnEquipmentToAssign({ userID: null }));
    }
  }

  hasSelectedAssignmentTarget() {
    return this.assignmentTargetType === 'campus'
      ? !!this.getSelectedProgramID()
      : !!this.getSelectedUserID();
  }

  private getSelectedUserID() {
    return this.normalizeUserID(this.userID)
      ?? this.normalizeUserID(this.assigningEquipments?.[0]?.userID);
  }

  private getSelectedProgramID() {
    return this.normalizeProgramID(this.assignedProgramID)
      ?? this.normalizeProgramID(this.assigningEquipments?.[0]?.assignedProgramID);
  }

  private normalizeUserID(userID: any) {
    const parsedUserID = Number(userID);
    return Number.isFinite(parsedUserID) && parsedUserID > 0 ? parsedUserID : null;
  }

  private normalizeProgramID(programID: any) {
    const parsedProgramID = Number(programID);
    return Number.isFinite(parsedProgramID) && parsedProgramID > 0 ? parsedProgramID : null;
  }

  get disableAssignControls() {
    return !!this.savingAssignEquipments;
  }

  getSelectedAssigneeLabel() {
    if (this.assignmentTargetType === 'campus') {
      const selectedProgramID = this.getSelectedProgramID();
      if (!selectedProgramID) {
        return '-';
      }

      return this.campusesList.find(x => x.value === selectedProgramID)?.label ?? '-';
    }

    const selectedUserID = this.getSelectedUserID();
    if (!selectedUserID) {
      return '-';
    }

    return this.usersList.find(x => x.value === selectedUserID)?.label ?? '-';
  }

  private buildGroupFilterOptions(groups: string[]): SelectItem[] {
    const normalizedGroups = (groups ?? [])
      .map(group => `${group ?? ''}`.trim())
      .filter(group => group.length > 0)
      .sort((a, b) => a.localeCompare(b));
    return [
      { label: 'No group filter', value: '' },
      ...normalizedGroups.map(group => ({ label: group, value: group }))
    ];
  }

  private loadAssignableUsers() {
    this.usersList = [{ label: 'loading...', value: undefined }];
    this.manageAssetService.getAssignableEmployees(this.includeTerminated).subscribe({
      next: (employees) => {
        this.usersList = [
          { label: '', value: null },
          ...(employees ?? []).map(x => ({ label: x.fullName, value: x.id }))
        ];
        this.ensureSelectedUserAssignable();
      },
      error: () => {
        this.usersList = [{ label: '', value: null }];
      }
    });
  }

  private ensureSelectedUserAssignable() {
    const normalizedUserID = this.normalizeUserID(this.userID);
    if (!normalizedUserID) {
      return;
    }

    const existsInVisibleList = this.usersList.some(x => x.value === normalizedUserID);
    if (existsInVisibleList || this.includeTerminated) {
      return;
    }

    this.includeTerminated = true;
    this.loadAssignableUsers();
  }
}

